import {
  Text,
  InlineStack,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  Spinner,
  Icon,
} from '@customerio/pluma-components/react';
import { generate } from '@customerio/hydra-copilot';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import type { ServicesApiEnvHealthResponse } from '@/services/api/services-api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useWorkspaceHealthSimulation, useEnvHealth } from '@/features/workspace-health';
import { useFeatureFlag } from '@/services/auth/feature-flags';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';

const summarySchema = z.toJSONSchema(
  z.object({
    summary: z.string().describe('A 2-4 sentence plain-English summary of the health status'),
  }),
);

const prompt = `You are summarizing the health status of a Customer.io workspace for a non-technical user.

Write a 2-4 sentence summary in plain English based on the health data provided in context.

## Processing categories (ingress — data coming into the system)

Each category has a "pending" count (items waiting) and optional "delay_seconds" (estimated time to clear the backlog).

- identify: Profile creates/updates. Backlog means new or updated customer data is delayed.
- events: Tracking events. Backlog means events are queued and journeys triggered by them may start late.
- campaigns: Journey actions (emails, webhooks, etc.) being evaluated and scheduled. Backlog means journey messages will be delayed.
- broadcasts: One-time bulk sends. Backlog means a broadcast is still being processed.
- transactional: Transactional messages (password resets, receipts, etc.). Backlog means time-sensitive messages are delayed.
- delivery: Email rendering, click tracking setup, and handoff to delivery providers. Backlog means messages are waiting to be sent.
- csv_imports: CSV file imports. Backlog means an import is still in progress.
- integrations: Data flowing to/from connected integrations. Backlog means integration syncs are delayed.
- objects: Object profile creates/updates (non-person entities). Backlog means object data is delayed.
- segments: Segment rebuilds and static segment membership changes. Backlog means segment membership is temporarily stale.

## Egress categories (outbound — data leaving the system)

- message_delivery: Final delivery of campaign and broadcast messages to providers (email, push, SMS). Backlog means messages are queued for delivery.
- transactional_delivery: Final delivery of transactional messages. Backlog means transactional messages are waiting to be sent.
- webhooks: Outbound webhooks and journey webhook actions. Backlog means webhooks are firing late.
- journey_actions: Non-message journey actions (attribute changes, API calls). Backlog means journey steps are delayed.
- alias: Identity resolution merges. Backlog means profile merges are delayed.
- related_updates: Cascading updates from relationships between objects and people. Backlog means related data is catching up.

## Rules

- Start with the overall status: everything is running smoothly, things are busy but under control, or there are problems that may need attention.
- Don't mention the score explicitly. Instead, use it to guide the tone of the summary.
- If busy or degraded: explain user-visible impact (e.g., "messages may be delayed by about 7 minutes"). Use approximate times derived from delay_seconds.
- Only mention categories that have a meaningful backlog. Do not list categories that are at zero.
- If there are failed tasks, mention them briefly.
- If segments_paused is true, explain that segment updates are temporarily on hold and will resume automatically.
- Never use jargon like "drain seconds", "pending count", "egress", "throughput", or "backfill".
- Plain text only. No markdown. Be concise.`;

function healthFingerprint(data: ServicesApiEnvHealthResponse): string {
  const processingBucket =
    data.processing.total_pending > 0 ? Math.floor(Math.log10(data.processing.total_pending)) : 0;
  const egressBucket = data.egress.total_pending > 0 ? Math.floor(Math.log10(data.egress.total_pending)) : 0;
  const failedCount = data.tasks.failed.length;
  return `${data.health}-${processingBucket}-${egressBucket}-${failedCount}`;
}

export function AISummaryPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const data = healthQuery.data;
  const fingerprint = data != null ? healthFingerprint(data) : '';

  const isHealthy = (data?.health ?? 'healthy') === 'healthy';

  const aiEnabled = useFeatureFlag(AccountFeatureFlag.AiEnabled);

  const summaryQuery = useQuery({
    queryKey: ['env-health-summary', fingerprint],
    queryFn: async () => {
      const result = await generate(prompt, summarySchema, {
        context: [{ description: 'Current health status data', value: data }],
        includeGlobalContext: true,
        includeUserContext: true,
      });
      return result.summary as string;
    },
    staleTime: 5 * 60_000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: aiEnabled && data != null && isHealthy === false,
  });

  if (summaryQuery.isError || aiEnabled === false || isHealthy === true) {
    return null;
  }

  return (
    <Panel>
      <PanelHeader>
        <InlineStack gap="050" alignBlock="center">
          <PanelTitle>Summary</PanelTitle>
          <Icon name="sparkle" />
        </InlineStack>
      </PanelHeader>
      <PanelSection>
        <InlineStack gap="100">
          {summaryQuery.isLoading ? (
            <Spinner shouldShowLoadingText label="Generating summary" />
          ) : (
            <Text size="s">{summaryQuery.data}</Text>
          )}
        </InlineStack>
      </PanelSection>
    </Panel>
  );
}
